import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  ExpenseDetailBody,
  type ExpenseDetail,
} from '@/components/spending/expense-detail-body';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate } from '@/utils/format';

const statusBadgeVariant = (status: string): BadgeVariant => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'approved':
      return 'brand';
    case 'pending_approval':
      return 'warning';
    case 'rejected':
    case 'void':
      return 'danger';
    default:
      return 'neutral';
  }
};

export const ExpenseDetailDialog = ({
  expenseId,
  open,
  onOpenChange,
  canApprove = false,
  onWorkflowAction,
}: {
  expenseId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canApprove?: boolean;
  onWorkflowAction?: (action: 'reject' | 'approve' | 'pay') => void;
}) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const locale = usePreferences((state) => state.locale);

  const detail = useQuery({
    queryKey: ['expenses', expenseId],
    queryFn: () => api.get<ExpenseDetail>(`/expenses/${expenseId}`),
    enabled: open && Boolean(expenseId),
  });

  const expenseDate = detail.data?.expenseDate
    ? formatDate(detail.data.expenseDate, { locale, style: 'medium' })
    : null;

  const showReject = canApprove && detail.data?.status === 'pending_approval';
  const showApprove = canApprove && detail.data?.status === 'pending_approval';
  const showPay = canApprove && detail.data?.status === 'approved';
  const hasWorkflowActions = showReject || showApprove || showPay;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" className="overflow-visible" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <span>
              {expenseDate
                ? t('expenses.detail.dialogTitleDated', { date: expenseDate })
                : t('expenses.detail.dialogTitle')}
            </span>
            {detail.data ? (
              <Badge variant={statusBadgeVariant(detail.data.status)}>
                {t(`expenses.status.${detail.data.status}`, { defaultValue: detail.data.status })}
              </Badge>
            ) : null}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {expenseId ? (
            <ExpenseDetailBody expenseId={expenseId} linkOut={false} showIntro={false} />
          ) : null}
        </DialogBody>
        <DialogFooter className="justify-end gap-2">
          {showReject ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => onWorkflowAction?.('reject')}
            >
              {t('expenses.reject')}
            </Button>
          ) : null}
          {showApprove ? (
            <Button type="button" onClick={() => onWorkflowAction?.('approve')}>
              {t('expenses.approve')}
            </Button>
          ) : null}
          {showPay ? (
            <Button type="button" onClick={() => onWorkflowAction?.('pay')}>
              {t('expenses.pay')}
            </Button>
          ) : null}
          <Button
            type="button"
            variant={hasWorkflowActions ? 'outline' : 'secondary'}
            onClick={() => onOpenChange(false)}
          >
            {tCommon('action.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
